import dayjs, { type Dayjs } from "dayjs";
import {
  buildDataPointsFilter,
  buildDayRange,
  buildPeriodRange,
  buildSleepPeriodRange,
  buildSleepRange,
} from "./buildDataPointsFilter";
import { getGoogleHealthDataPoints } from "./getGoogleHealthDataPoints";
import type {
  GoogleHealthDataType,
  IGoogleHealthAuthContext,
  IGoogleHealthDataPoint,
  IGoogleHealthFitnessBundle,
  IGoogleHealthTimeRange,
} from "./types";
import {
  GoogleHealthScopeError,
  isGoogleHealthScopeError,
  toGoogleHealthScopeError,
} from "./googleHealthErrors";

export interface IGoogleHealthFetchError {
  dataType: GoogleHealthDataType;
  error: unknown;
}

export interface IGoogleHealthFitnessBundleResult {
  bundle: IGoogleHealthFitnessBundle;
  errors: IGoogleHealthFetchError[];
  /** Scope insuficiente quando nenhum dado foi carregado (reconnect). */
  scopeError?: GoogleHealthScopeError | null;
}

export interface IFetchGoogleHealthFitnessBundleParams extends IGoogleHealthAuthContext {
  /** Último dia do período (âncora). Mantido para compatibilidade com busca de 1 dia. */
  selectedDate: Dayjs;
  /** Início do período (inclusive). Se omitido, usa `selectedDate`. */
  periodStart?: Dayjs;
  /** Fim do período (inclusive). Se omitido, usa `selectedDate`. */
  periodEnd?: Dayjs;
}

function filterEcgWithinRange(
  points: IGoogleHealthDataPoint[],
  range: IGoogleHealthTimeRange
): IGoogleHealthDataPoint[] {
  return points.filter((point) => {
    const start = point.electrocardiogram?.interval?.startTime;
    if (!start) return false;
    const startMs = dayjs(start).valueOf();
    return startMs >= range.startMs && startMs < range.endMs;
  });
}

/**
 * Consulta em paralelo os endpoints v4 usados pelo dashboard fitness.
 * Falhas parciais não impedem exibir métricas disponíveis; erro de scope propaga reconnect.
 */
export async function fetchGoogleHealthFitnessBundle(
  params: IFetchGoogleHealthFitnessBundleParams
): Promise<IGoogleHealthFitnessBundleResult> {
  const { selectedDate, periodStart, periodEnd, ...auth } = params;
  const rangeEnd = (periodEnd ?? selectedDate).startOf("day");
  const rangeStart = (periodStart ?? selectedDate).startOf("day");
  const isSingleDay = rangeStart.isSame(rangeEnd, "day");

  const dayRange = isSingleDay
    ? buildDayRange(rangeEnd)
    : buildPeriodRange(rangeStart, rangeEnd);
  const sleepRange = isSingleDay
    ? buildSleepRange(rangeEnd)
    : buildSleepPeriodRange(rangeStart, rangeEnd);

  const requests = [
    {
      dataType: "steps" as const,
      filter: buildDataPointsFilter("steps", dayRange),
    },
    {
      dataType: "distance" as const,
      filter: buildDataPointsFilter("distance", dayRange),
    },
    {
      dataType: "sleep" as const,
      filter: buildDataPointsFilter("sleep", sleepRange),
    },
    {
      dataType: "electrocardiogram" as const,
      filter: buildDataPointsFilter("electrocardiogram", dayRange),
    },
    {
      dataType: "heart-rate" as const,
      filter: buildDataPointsFilter(
        "heart-rate",
        dayRange,
        isSingleDay ? rangeEnd : undefined
      ),
    },
    {
      dataType: "weight" as const,
      filter: buildDataPointsFilter(
        "weight",
        dayRange,
        isSingleDay ? rangeEnd : undefined
      ),
    },
  ];

  const results = await Promise.allSettled(
    requests.map(({ dataType, filter }) =>
      getGoogleHealthDataPoints({ ...auth, dataType, filter })
    )
  );

  const errors: IGoogleHealthFetchError[] = [];
  let scopeError: GoogleHealthScopeError | null = null;
  const dataByType: Record<GoogleHealthDataType, IGoogleHealthDataPoint[]> = {
    steps: [],
    distance: [],
    sleep: [],
    electrocardiogram: [],
    "heart-rate": [],
    weight: [],
  };

  results.forEach((result, index) => {
    const dataType = requests[index].dataType;
    if (result.status === "fulfilled") {
      dataByType[dataType] = result.value;
      return;
    }

    errors.push({ dataType, error: result.reason });
    if (isGoogleHealthScopeError(result.reason)) {
      scopeError = toGoogleHealthScopeError(result.reason);
    }
    console.warn(`Google Health (${dataType}):`, result.reason);
  });

  const electrocardiogram = filterEcgWithinRange(
    dataByType.electrocardiogram,
    dayRange
  );

  const bundle: IGoogleHealthFitnessBundle = {
    steps: dataByType.steps,
    distance: dataByType.distance,
    sleep: dataByType.sleep,
    electrocardiogram,
    heartRate: dataByType["heart-rate"],
    weight: dataByType.weight,
    queriedAt: new Date().toISOString(),
    // Sinaliza fetch diário: só nesse caso os fallbacks "soma tudo" podem rodar.
    coversSingleDay: isSingleDay,
  };

  const hasAnyData =
    bundle.steps.length > 0 ||
    bundle.distance.length > 0 ||
    bundle.sleep.length > 0 ||
    bundle.electrocardiogram.length > 0 ||
    bundle.heartRate.length > 0 ||
    bundle.weight.length > 0;

  if (scopeError && !hasAnyData) {
    throw scopeError;
  }

  if (
    errors.length === requests.length &&
    !hasAnyData
  ) {
    const firstError = errors[0]?.error;
    if (firstError instanceof GoogleHealthScopeError) throw firstError;
    throw firstError ?? new Error("Não foi possível consultar dados do Google Health.");
  }

  return { bundle, errors, scopeError: scopeError && !hasAnyData ? scopeError : null };
}
